import type { FeatureCollection, LineString, Point } from 'geojson'
import type { DriveDetectionModel, DriveSignPostModel, DrivenStreetSegmentModel } from '@/api/main/types.gen'

/** Sign post detections sharing exactly the same position (e.g. the same sign post seen repeatedly). */
export type SignPostStack = {
  key: string
  longitude: number
  latitude: number
  // Most recent detection first
  signPosts: DriveSignPostModel[]
}

const positionKey = (longitude: number, latitude: number) => `${longitude},${latitude}`

export function stackSignPosts(signPosts: DriveSignPostModel[]): SignPostStack[] {
  const stacks = new Map<string, SignPostStack>()
  for (const signPost of signPosts) {
    const { longitude, latitude } = signPost.position
    const key = positionKey(longitude, latitude)
    const stack = stacks.get(key)
    if (stack) {
      stack.signPosts.push(signPost)
    } else {
      stacks.set(key, { key, longitude, latitude, signPosts: [signPost] })
    }
  }
  for (const stack of stacks.values()) {
    stack.signPosts.sort((a, b) => b.observed_at.localeCompare(a.observed_at))
  }
  return Array.from(stacks.values())
}

export function signPostStacksToGeoJSON(stacks: SignPostStack[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: stacks.map(stack => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [stack.longitude, stack.latitude] },
      properties: { key: stack.key, count: stack.signPosts.length },
    })),
  }
}

export function detectionsToGeoJSON(detections: DriveDetectionModel[]): FeatureCollection<LineString> {
  return {
    type: 'FeatureCollection',
    features: detections.map((detection, index) => ({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [detection.rear_center.longitude, detection.rear_center.latitude],
          [detection.front_center.longitude, detection.front_center.latitude],
        ],
      },
      properties: { index },
    })),
  }
}

function lineStringFromUnknown(value: unknown): LineString | null {
  if (!value || typeof value !== 'object') return null
  const geometry = value as { type?: unknown; coordinates?: unknown }
  if (geometry.type !== 'LineString' || !Array.isArray(geometry.coordinates)) return null
  return geometry as LineString
}

export function drivenSegmentsToGeoJSON(
  segments: DrivenStreetSegmentModel[],
  highlightedIndex: number | null
): FeatureCollection<LineString> {
  const features: FeatureCollection<LineString>['features'] = []
  segments.forEach((segment, index) => {
    const geometry = lineStringFromUnknown(segment.segment_geojson)
    if (!geometry) return
    features.push({
      type: 'Feature',
      geometry,
      properties: { index, highlighted: index === highlightedIndex },
    })
  })
  // Draw the highlighted segment last so that it ends up on top
  features.sort((a, b) => Number(a.properties!.highlighted) - Number(b.properties!.highlighted))
  return { type: 'FeatureCollection', features }
}

export function segmentMidpoint(segment: DrivenStreetSegmentModel): { longitude: number; latitude: number } | null {
  const geometry = lineStringFromUnknown(segment.segment_geojson)
  if (!geometry || geometry.coordinates.length === 0) return null
  const [longitude, latitude] = geometry.coordinates[Math.floor(geometry.coordinates.length / 2)]
  return { longitude, latitude }
}

/**
 * Pixel offsets that spread `count` markers evenly on a circle around their shared position.
 * The radius grows with the count so that neighbouring markers do not overlap.
 */
export function explodeOffsets(count: number, markerSpacing = 22, minRadius = 28): Array<[number, number]> {
  if (count <= 1) return [[0, 0]]
  const radius = Math.max(minRadius, (markerSpacing * count) / (2 * Math.PI))
  return Array.from({ length: count }, (_, i) => {
    // Start at 12 o'clock and go clockwise
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count
    return [Math.round(radius * Math.cos(angle)), Math.round(radius * Math.sin(angle))]
  })
}
